/**
 * <park-page-header> — shared page title + optional intro/lead block
 * (epic #138, issue #187). Isomorphic: the pure renderParkPageHeader() below is
 * shared by the SSR wrapper components/ds/ParkPageHeader.astro and the custom
 * element (see docs/design-system-rebuild-plan.md → "Isomorphic contract").
 *
 * Reproduces the live `.park-page__header` (+ h1) used by content pages, with an
 * optional `.park-lead` intro. Emits the shared classes (in park.css) — no
 * per-component CSS.
 *
 * Attributes:
 *   - title="…"   the page heading (rendered as <h1>). Required to render.
 *   - lead="…"    optional intro paragraph (.park-lead). Omitted → no <p>.
 * Slotted content = none (attribute-driven; values are esc()'d).
 *
 * @dsSpec design-system#FR-3 — the shared page title + lead block used by every content page.
 *   (Traceability: specs/design-system/traceability.md; the generated card
 *   carries this same ref in its @dsCard marker — anton/forrest#846.)
 */

import { ParkElement, defineElement, esc, html } from "./base";

const TAG = "park-page-header";

export interface ParkPageHeaderProps {
	title?: string;
	lead?: string;
	/** Cap the intro at 60ch (matches the ukk page's intro). Default full width. */
	narrow?: boolean;
}

/** Pure render: props → inner HTML string. No DOM access (server + client safe).
 * Emits a bare <h1> (global h1 size/margin/font) + a plain intro <p> at body size
 * — pixel-equivalent to the content pages' hand-written headers (#150). */
export function renderParkPageHeader(props: ParkPageHeaderProps): string {
	const title = props.title ?? "";
	const titleHtml = title
		? html`<h1 class="park-page-title">${esc(title)}</h1>`
		: "";
	const introCls = props.narrow
		? "park-page-intro park-page-intro--narrow"
		: "park-page-intro";
	const leadHtml = props.lead
		? html`<p class="${introCls}">${esc(props.lead)}</p>`
		: "";
	return `${titleHtml}${leadHtml}`;
}

class ParkPageHeader extends ParkElement {
	static get observedAttributes(): string[] {
		return ["title", "lead", "narrow"];
	}

	protected render(): string {
		return renderParkPageHeader({
			title: this.attr("title"),
			lead: this.attr("lead"),
			narrow: this.boolAttr("narrow"),
		});
	}
}

defineElement(TAG, ParkPageHeader);

export { ParkPageHeader };
